import { useState } from 'react';
import './DesignSpecPage.css';

const FINISHES = [
  { slug: 'polished', label: 'Polished', image: '/Polished.webp',     description: 'Mirror-like, high-gloss surface achieved through mechanical buffing. Reflects light dramatically and accentuates depth of colour and veining. Ideal for statement countertops and feature walls.', care: 'Wipe with damp cloth. Avoid abrasive cleaners.', bestFor: ['Kitchen Worktops', 'Bathroom Vanities', 'Reception Desks'] },
  { slug: 'natural',  label: 'Natural',  image: '/Natural.webp',      description: 'Satin-smooth, low-sheen surface with an organic feel that closely mimics raw stone. Softer on the eye than polished — warmth without the glare.', care: 'Damp cloth. Mild soap for stubborn marks.', bestFor: ['Kitchen Worktops', 'Flooring', 'Furniture'] },
  { slug: 'honed',    label: 'Honed',    image: '/Honed.webp',        description: 'Matte, flat finish ground to a smooth texture without polish. Understated luxury — hides fingerprints better than polished, giving a contemporary architectural feel.', care: 'Regular wiping with a damp cloth. Occasional pH-neutral cleaner.', bestFor: ['Bathroom Vanities', 'Flooring & Walls', 'Commercial Spaces'] },
  { slug: 'concrete', label: 'Concrete', image: '/Concrete.webp',     description: 'Industrial-inspired finish with a raw, grainy texture that evokes cast concrete. Adds character and depth to minimalist spaces.', care: 'Brush off loose particles. Damp cloth for deeper clean.', bestFor: ['Commercial Spaces', 'Flooring', 'Kitchen Worktops'] },
  { slug: 'silk',     label: 'Silk',     image: '/Silk.webp',         description: 'Ultra-refined finish with a subtle sheen — between polished and natural. Smooth, soft luminosity that reads differently under natural vs. artificial light.', care: 'Standard damp-cloth maintenance. Avoid harsh abrasives.', bestFor: ['Bathroom Vanities', 'Furniture', 'Hospitality'] },
  { slug: 'leather',  label: 'Leather',  image: '/Ultra_rough.webp',  description: 'Textured, pitted finish that replicates the grain of natural leather. Hides scratches and etching exceptionally well — practical for high-traffic surfaces.', care: 'Soft brush or damp cloth. Avoid pressure washing.', bestFor: ['Outdoor Surfaces', 'Commercial Spaces', 'Flooring'] },
];

const SLAB_FORMATS = [
  { brand: 'Caesarstone', formats: [{ label: 'Standard', mm: '3050 × 1440', inches: '120.1 × 56.7' }, { label: 'Jumbo', mm: '3200 × 1600', inches: '126.0 × 63.0' }], thicknesses: ['12mm', '20mm', '30mm'], outdoor: false },
  { brand: 'Dekton',      formats: [{ label: 'Standard', mm: '3200 × 1440', inches: '126.0 × 56.7' }, { label: 'Jumbo', mm: '3360 × 1500', inches: '132.3 × 59.1' }], thicknesses: ['8mm', '12mm', '20mm', '30mm'], outdoor: true },
  { brand: 'Scalea',      formats: [{ label: 'Standard', mm: '3000 × 1500', inches: '118.1 × 59.1' }, { label: 'Large', mm: '3300 × 1650', inches: '129.9 × 65.0' }], thicknesses: ['20mm', '30mm'], outdoor: true },
  { brand: 'Sensa',       formats: [{ label: 'Standard', mm: '3000 × 1500', inches: '118.1 × 59.1' }], thicknesses: ['20mm', '30mm'], outdoor: false },
  { brand: 'Atlas Plan',  formats: [{ label: 'Standard', mm: '3200 × 1600', inches: '126.0 × 63.0' }, { label: 'XXL', mm: '3600 × 1200', inches: '141.7 × 47.2' }], thicknesses: ['6mm', '12mm', '20mm'], outdoor: true },
];

const THICKNESS_CARDS = [
  { mm: '6mm',  uses: ['Wall Cladding', 'Furniture Panels', 'Decorative Facades'], brands: ['Atlas Plan'], note: 'Atlas Plan only — requires substrate backing', inHouse: false },
  { mm: '8mm',  uses: ['Feature Walls', 'Splashbacks', 'Lightweight Cladding'], brands: ['Dekton'], note: 'Dekton only — not for structural countertop use', inHouse: false },
  { mm: '12mm', uses: ['Kitchen Worktops (light use)', 'Bathroom Vanities', 'Furniture Tops', 'Splashbacks'], brands: ['Dekton', 'Caesarstone', 'Atlas Plan'], note: null, inHouse: false },
  { mm: '20mm', uses: ['Kitchen Worktops', 'Bathroom Vanities', 'Island Tops', 'Dining Tables', 'Reception Desks'], brands: ['Dekton', 'Caesarstone', 'Scalea', 'Sensa', 'Atlas Plan'], note: 'Fabricated & edge-profiled in-house at our Nairobi workshop', inHouse: true },
  { mm: '30mm', uses: ['Statement Kitchen Islands', 'Commercial Counters', 'High-Traffic Surfaces', 'Outdoor Kitchens'], brands: ['Dekton', 'Caesarstone', 'Scalea', 'Sensa'], note: 'Fabricated & edge-profiled in-house at our Nairobi workshop', inHouse: true },
];

const EDGE_PROFILES = [
  { id: 'eased',    name: 'Eased',                    popular: true,  image: '/eased.svg',                          description: '90° top corner lightly broken. The most common and cost-effective profile.' },
  { id: 'bevel',    name: 'Bevel',                    popular: false, image: '/bevel.svg',                          description: 'Top edge cut at 45° angle. Clean, contemporary look.' },
  { id: 'dbevel',   name: 'Double Bevel',              popular: false, image: '/double%20bevel.svg',                 description: 'Both top and bottom edges bevelled at 45°. Symmetrical and modern.' },
  { id: 'fbull',    name: 'Full Bullnose',             popular: true,  image: '/full%20bullnose.svg',                description: 'Top edge fully rounded to a semicircle. Soft, family-friendly.' },
  { id: 'dbull',    name: 'Double Bullnose',           popular: false, image: '/double%20bullnose.svg',              description: 'Top and bottom edges both fully rounded. Elegant thick appearance.' },
  { id: 'dradius',  name: 'Double Radius',             popular: false, image: '/double%20radius.svg',                description: 'Top and bottom edges with a small quarter-round radius each side.' },
  { id: 'pencil',   name: 'Pencil',                   popular: false, image: '/pencil.svg',                         description: 'A very small radius on the top edge — subtle refinement over eased.' },
  { id: 'ogee',     name: 'Ogee',                     popular: false, image: '/ogee.svg',                           description: 'S-shaped decorative profile — classic and ornate. Traditional kitchens.' },
  { id: 'triple',   name: 'Triple Laminated Bullnose', popular: false, image: '/Triple%20laminated%20bullnose.svg',  description: 'Three layers stacked for a dramatic thick edge — 60mm+ appearance.' },
  { id: 'mitered',  name: 'Mitered',                  popular: false, image: '/miter.svg',                          description: 'Two slabs joined at 45° for a seamless waterfall edge. Contemporary.' },
];

const BRAND_COMPARISON = [
  { property: 'Scratch Resistance', dekton: '★★★★★', caesarstone: '★★★★☆', scalea: '★★★☆☆', sensa: '★★★☆☆', atlas: '★★★★☆' },
  { property: 'Heat Resistance',    dekton: '★★★★★', caesarstone: '★★★☆☆', scalea: '★★★★☆', sensa: '★★★★☆', atlas: '★★★★☆' },
  { property: 'UV / Outdoor Use',   dekton: '✓',      caesarstone: '✗',      scalea: '✓',      sensa: '✗',      atlas: '✓' },
  { property: 'Stain Resistance',   dekton: '★★★★★', caesarstone: '★★★★★', scalea: '★★★☆☆', sensa: '★★★★☆', atlas: '★★★★☆' },
  { property: 'Porosity',           dekton: 'Zero',   caesarstone: 'Near Zero', scalea: 'Low–Medium', sensa: 'Low (protected)', atlas: 'Zero' },
  { property: 'Warranty',           dekton: '25 yrs', caesarstone: '25 yrs', scalea: '10 yrs', sensa: '15 yrs', atlas: '10 yrs' },
];

const NAV_SECTIONS = [
  { label: 'Finishes',         id: 'dsp-finishes' },
  { label: 'Slab Formats',     id: 'dsp-slab-formats' },
  { label: 'Edge Profiles',    id: 'dsp-edge-profiles' },
  { label: 'Brand Comparison', id: 'dsp-brand-comparison' },
];

const DesignSpecPage = ({ onClose }) => {
  const [activeFinish, setActiveFinish] = useState(FINISHES[0]);
  const [unit, setUnit] = useState('mm');
  const selectFinish = (f) => {
    setActiveFinish(f);
    setTimeout(() => {
      document.getElementById('dsp-finishes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="dsp-overlay">
      {/* Sticky top bar */}
      <div className="dsp-topbar">
        <button className="dsp-back-btn" onClick={onClose}>
          <span>←</span> Back
        </button>
        <div className="dsp-topbar-nav">
          {NAV_SECTIONS.map(s => (
            <button key={s.id} className="dsp-topbar-pill" onClick={() => scrollTo(s.id)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="dsp-content">
        {/* Hero */}
        <section className="dsp-hero">
          <p className="dsp-eyebrow">Technical Reference</p>
          <h1 className="dsp-hero-title">Design &amp; Specification</h1>
          <p className="dsp-hero-sub">
            Surface finishes, slab formats, thickness options, and edge profiles — everything you need to specify the right material for your project.
          </p>
        </section>

        {/* 01 Finishes */}
        <section className="dsp-section" id="dsp-finishes">
          <div className="dsp-section-head">
            <p className="dsp-section-num">01</p>
            <div>
              <h2 className="dsp-section-title">Finishes &amp; Textures</h2>
              <p className="dsp-section-hint">Tap a swatch to learn more about each finish.</p>
            </div>
          </div>

          <div className="dsp-finishes-layout">
            <div className="dsp-finish-grid">
              {FINISHES.map(f => (
                <button
                  key={f.slug}
                  className={`dsp-finish-swatch${activeFinish.slug === f.slug ? ' active' : ''}`}
                  onClick={() => selectFinish(f)}
                >
                  <img className="dsp-finish-swatch-img" src={f.image} alt={f.label} />
                  <span className="dsp-finish-swatch-label">{f.label}</span>
                </button>
              ))}
            </div>

            <div className="dsp-finish-detail">
              <img className="dsp-finish-detail-preview" src={activeFinish.image} alt={activeFinish.label} />
              <p className="dsp-finish-detail-eyebrow">Surface Finish</p>
              <h3 className="dsp-finish-detail-name">{activeFinish.label}</h3>
              <p className="dsp-finish-detail-desc">{activeFinish.description}</p>
              <p className="dsp-finish-detail-sub">Care &amp; Maintenance</p>
              <p className="dsp-finish-detail-care">{activeFinish.care}</p>
              <p className="dsp-finish-detail-sub">Best For</p>
              <div className="dsp-finish-tags">
                {activeFinish.bestFor.map(b => <span key={b} className="dsp-finish-tag">{b}</span>)}
              </div>
            </div>
          </div>
        </section>

        {/* 02 Slab Formats */}
        <section className="dsp-section" id="dsp-slab-formats">
          <div className="dsp-section-head dsp-section-head--row">
            <div>
              <p className="dsp-section-num">02</p>
              <h2 className="dsp-section-title">Slab Formats</h2>
            </div>
            <div className="dsp-unit-toggle">
              <button className={unit === 'mm' ? 'active' : ''} onClick={() => setUnit('mm')}>MM</button>
              <button className={unit === 'in' ? 'active' : ''} onClick={() => setUnit('in')}>IN</button>
            </div>
          </div>

          <div className="dsp-formats-scroll">
            <table className="dsp-formats-table">
              <thead>
                <tr>
                  <th>Brand</th><th>Format</th><th>Dimensions ({unit})</th><th>Thickness</th><th>Outdoor</th>
                </tr>
              </thead>
              <tbody>
                {SLAB_FORMATS.map(brand =>
                  brand.formats.map((fmt, i) => (
                    <tr key={`${brand.brand}-${fmt.label}`} className={i === 0 ? 'dsp-brand-first-row' : ''}>
                      <td className="dsp-td-brand">{i === 0 ? brand.brand : ''}</td>
                      <td className="dsp-td-format">{fmt.label}</td>
                      <td className="dsp-td-dim">{unit === 'mm' ? fmt.mm : fmt.inches}</td>
                      <td className="dsp-td-thick">
                        {i === 0 && brand.thicknesses.map(t => <span key={t} className="dsp-thick-chip">{t}</span>)}
                      </td>
                      <td className={`dsp-td-outdoor${brand.outdoor ? ' yes' : ' no'}`}>
                        {i === 0 ? (brand.outdoor ? '✓' : '✗') : ''}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <p className="dsp-table-note">* Dimensions and thicknesses vary by colour and collection. Confirm availability with our team before specifying.</p>
          </div>

          {/* Thickness Guide */}
          <div className="dsp-thick-section">
            <p className="dsp-thick-eyebrow">Thickness Guide</p>
            <h3 className="dsp-thick-title">Which thickness is right for your application?</h3>
            <div className="dsp-thick-scroll">
              {THICKNESS_CARDS.map(card => (
                <div key={card.mm} className="dsp-thick-card">
                  {card.inHouse && <span className="dsp-inhouse-badge">In-House</span>}
                  <h4 className="dsp-thick-mm">{card.mm}</h4>
                  <ul className="dsp-thick-uses">
                    {card.uses.map(u => <li key={u}>{u}</li>)}
                  </ul>
                  <div className="dsp-thick-brands">
                    {card.brands.map(b => <span key={b} className="dsp-thick-brand-chip">{b}</span>)}
                  </div>
                  {card.note && <p className="dsp-thick-note">{card.note}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 03 Edge Profiles */}
        <section className="dsp-section" id="dsp-edge-profiles">
          <p className="dsp-section-num">03</p>
          <h2 className="dsp-section-title">Edge Profiles</h2>
          <p className="dsp-section-sub">Edge profiling is fabricated on-site by our CNC team. Lead times and pricing vary by profile complexity and slab thickness.</p>
          <div className="dsp-edge-grid">
            {EDGE_PROFILES.map(ep => (
              <div key={ep.id} className="dsp-edge-card">
                <div className="dsp-edge-svg-wrap">
                  <img src={ep.image} alt={ep.name} className="dsp-edge-svg-img" />
                </div>
                <div className="dsp-edge-body">
                  <div className="dsp-edge-name-row">
                    <span className="dsp-edge-name">{ep.name}</span>
                    {ep.popular && <span className="dsp-edge-popular">Popular</span>}
                  </div>
                  <p className="dsp-edge-desc">{ep.description}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="dsp-section-footnote">Minimum inside radius: 3mm for eased; 6mm for bullnose profiles. Consult our team for bespoke profiles.</p>
        </section>

        {/* 04 Brand Comparison */}
        <section className="dsp-section" id="dsp-brand-comparison">
          <p className="dsp-section-num">04</p>
          <h2 className="dsp-section-title">Brand Comparison</h2>
          <div className="dsp-compare-scroll">
            <table className="dsp-compare-table">
              <thead>
                <tr>
                  <th></th>
                  {['Dekton', 'Caesarstone', 'Scalea', 'Sensa', 'Atlas Plan'].map(b => (
                    <th key={b} className="dsp-compare-brand-head">{b}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {BRAND_COMPARISON.map(row => (
                  <tr key={row.property}>
                    <td className="dsp-compare-prop">{row.property}</td>
                    <td>{row.dekton}</td>
                    <td>{row.caesarstone}</td>
                    <td>{row.scalea}</td>
                    <td>{row.sensa}</td>
                    <td>{row.atlas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div style={{ height: 60 }} />
      </div>
    </div>
  );
};

export default DesignSpecPage;
